import { useState } from "react";
import { Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { DEFAULT_CATEGORY_LIST } from "@/lib/categories";

export default function AdminCategories() {
  const { list, refresh } = useCategories();
  const [rows, setRows] = useState(null); // null = untouched; edits live here until Save
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const editing = rows !== null;
  const shown = rows ?? list;

  const edit = (i, patch) => setRows((shown).map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows(shown.filter((_, j) => j !== i));
  const addRow = () => setRows([...shown, { name: "", color: "#C8102E" }]);

  const save = async () => {
    if (!editing) return;
    if (shown.some((r) => !r.name.trim())) {
      toast.error("Every category needs a name — fill it or remove the row.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/categories", { categories: shown });
      setRows(null);
      await refresh();
      toast.success("Categories saved — forms and the board filter update immediately.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!confirmReset) {
      setConfirmReset(true);
      setTimeout(() => setConfirmReset(false), 4000);
      return;
    }
    setConfirmReset(false);
    setSaving(true);
    try {
      await api.put("/admin/categories", { categories: DEFAULT_CATEGORY_LIST });
      setRows(null);
      await refresh();
      toast.success("Back to the original 12 categories.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-16" data-testid="admin-categories">
      <p className="overline-label mb-2">Categories — the board's shared taxonomy</p>
      <p className="text-sm text-white/45 mb-6 max-w-2xl">
        These chips appear on casting forms, the public board filter and talent profiles. Rename, recolor,
        add or remove — nothing saves until you hit Save. Renaming does not retag castings already posted.
      </p>

      <div className="flex flex-col gap-2 mb-6">
        {shown.map((r, i) => (
          <div key={i} className="flex items-center gap-3 panel !p-3" data-testid={`admin-category-row-${i}`}>
            <input
              type="color"
              value={r.color}
              onChange={(e) => edit(i, { color: e.target.value })}
              className="w-9 h-9 bg-transparent border border-white/20 cursor-pointer shrink-0"
              data-testid={`admin-category-color-${i}`}
              title="Chip color"
            />
            <input
              value={r.name}
              onChange={(e) => edit(i, { name: e.target.value })}
              placeholder="Category name"
              className="field !py-2 flex-1"
              data-testid={`admin-category-name-${i}`}
            />
            <span
              className="font-grotesk text-[9px] uppercase tracking-[0.12em] border px-2 py-1 hidden sm:inline"
              style={{ borderColor: r.color, color: r.color }}
            >
              {r.name || "preview"}
            </span>
            <button
              onClick={() => removeRow(i)}
              className="text-white/40 hover:text-accent transition-colors shrink-0"
              data-testid={`admin-category-remove-${i}`}
              title="Remove (saves on Save)"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={addRow} className="btn-ghost !py-2.5 inline-flex items-center gap-2" data-testid="admin-category-add">
          <Plus size={14} /> Add category
        </button>
        {editing && (
          <>
            <button onClick={save} disabled={saving} className="btn-primary !py-2.5 disabled:opacity-40" data-testid="admin-categories-save">
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button onClick={() => setRows(null)} className="btn-ghost !py-2.5" data-testid="admin-categories-discard">
              Discard
            </button>
          </>
        )}
        <button
          onClick={resetDefaults}
          disabled={saving}
          className={`btn-ghost !py-2.5 inline-flex items-center gap-2 ${confirmReset ? "!border-accent !text-accent" : ""}`}
          data-testid="admin-categories-reset"
        >
          <RotateCcw size={13} /> {confirmReset ? "Sure? Restores the original 12" : "Restore defaults"}
        </button>
      </div>
    </section>
  );
}
